import { YieldFetcher } from '../../types'
import { fetchDefiLlamaApy } from './defillama'
import { apyToAprPercent } from '../../../utils/parsing'

/**
 * Binance Wrapped Beacon ETH (wBETH) intrinsic yield.
 *
 * wBETH is non-rebasing and value-accruing: `exchangeRate()` on the token
 * (same value on Ethereum and BNB — one oracle pushes both) is the
 * ETH-per-wBETH price, bumped once a day in the 23:45–00:15 UTC window.
 *
 * Binance publishes the matching daily series on a public, unauthenticated
 * endpoint:
 *
 *   GET https://www.binance.com/bapi/earn/v1/public/pos/cftoken/project
 *       /exchange-rate/history?startTime=<ms>&endTime=<ms>
 *
 * Each row is `{ exchangeRate, apr, calcDate }`, **newest first**. The
 * `exchangeRate` field matches the on-chain `exchangeRate()` to the last
 * digit, so the API and the chain are the same source.
 *
 * TRAP — `apr` is a nominal APR, **not** an APY, so it is used verbatim
 * (only ×100 to reach the package's percent convention). Verified against
 * the series itself: over a 90-day window the realised rate-delta
 * annualises to 2.4465 % against a median published `apr` of 2.435 %. Do
 * NOT run it through `apyToApr*` the way the neighbouring cbETH fetcher
 * does for Coinbase's endpoint — that would understate the rate.
 *
 * The window is bounded deliberately: called with no params the endpoint
 * returns 1000 rows (~2.7 years), which is a pointless payload in a
 * worker.
 *
 * Falls back to DefiLlama's `binance-staked-eth` pool, whose `apy` field
 * IS an APY and therefore does get converted.
 */

const HISTORY_URL =
  'https://www.binance.com/bapi/earn/v1/public/pos/cftoken/project/exchange-rate/history'

/** Asset-group key — must match `yieldKey` in the LST registry entries
 *  (one shared key: the rate is identical on Ethereum and BNB). The group is
 *  the same on both chains only because token-lists' `GROUP_ALIAS` HOLDS it
 *  there — it is not naturally consistent. The source lists disagree on the
 *  symbol's casing (`WBETH` on Ethereum, `wBETH` on BNB), so once the LST
 *  curation started winning the first-list-wins merge, chain 1 derived
 *  `Wrapped Binance Beacon ETH::WBETH` and split off from BNB. Two aliases
 *  (2026-08-13) pin both that form and Fuel's `wbETH::wbETH` to the key
 *  below; if either is ever removed, this constant stops matching Ethereum
 *  and the chain-1 intrinsic APR silently reads 0. */
const WBETH = 'Wrapped Binance Beacon ETH::wBETH'

// No bare-ticker alias is emitted: no token on any chain list carries
// `WBETH` as its assetGroup, so a second key would just be a dead row.

/** DefiLlama pool for wBETH on Ethereum — the BSC pool
 *  (`566c64cb-c1ec-4027-bb67-1e9326d6d48a`) carries the same number, so
 *  one fallback covers both registry entries. */
const LLAMA_POOL = '80b8bf92-b953-4c20-98ea-c9653ef2bb98'

const DAY_MS = 86_400_000
/** Look-back for the request — wide enough to survive the series lagging
 *  a day or two without dragging in the full history. */
const LOOKBACK_MS = 14 * DAY_MS
const TIMEOUT_MS = 8_000

interface RatePoint {
  exchangeRate: string
  apr: string
  calcDate: string
}

interface HistoryResponse {
  code?: string
  success?: boolean
  data?: RatePoint[]
}

export const wbethFetcher: YieldFetcher = {
  label: 'WBETH',
  fetch: async () => {
    const now = Date.now()
    const url = `${HISTORY_URL}?startTime=${now - LOOKBACK_MS}&endTime=${now}`

    try {
      const res: HistoryResponse = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(TIMEOUT_MS),
      }).then((r) => r.json())

      const points = res.data ?? []
      if (points.length > 0) {
        // Explicitly pick the newest row — the response happens to be
        // sorted newest-first today, but nothing documents that.
        const latest = points.reduce((a, b) =>
          Number(b.calcDate) > Number(a.calcDate) ? b : a,
        )
        // `apr` is a fraction (0.02344395 = 2.344 %) and already nominal.
        const apr = Number(latest.apr) * 100
        if (Number.isFinite(apr) && apr > 0) return { [WBETH]: apr }
      }
    } catch (e) {
      console.log('WBETH history failed, falling back to DefiLlama', e)
    }

    // DefiLlama reports an APY *percent* here, unlike Binance's own
    // fractional APR field — `apyToAprPercent` is percent-in/percent-out.
    const apy = await fetchDefiLlamaApy(LLAMA_POOL)
    return { [WBETH]: apyToAprPercent(apy) }
  },
}
